import { Trans } from "@lingui/react/macro";
import Config from "../config";
import PageDataFeed from "../page-data-feed";
import WadminApi, { makeAbortableGET, makeAbortablePOST, AbortableRequest } from "./wadmin-api";
import { Fragment, h, VNode } from "preact";
import { UserInfoResponse } from "../user";

/**
 * Interfacce per la tipizzazione dei dati
 */
/*
export interface UserWallet {
    idwallet: number;
    idstruttura: number;
    idcredenziale: number;
    wallet_type: "regular" | "bonus";
    wallet_subtype?: string;
    currency: string;
    balance: number | string;
    fido: number | string | null;
    tot_in: number | string | null;
    tot_out: number | string | null;
}
/*
export interface UserInfoResponse {
    idstruttura: number;
    idcredenziale: number;
    company: string;
    username: string;
    type: 'affiliate' | 'player';
    idparent: number | null;
    path: string;
    path_len: number;
    idaccess: number;
    tipo_accesso: number;
    preferred_currency: string;
    created: string | null;
    token: string | null;
    notifications: boolean;
    wallets: UserWallet[];
    bonus: any;
    ticketprint_url?: string;
}
*/
interface APIParams {
    isPOST(): boolean;
    getQueryString(): string;
    getFormData(): FormData | null;
}

class APIPort {
    request<T = any>(url: string, apiParams: APIParams, opts: RequestInit = {}): Promise<T | void> {
        return this._makeRequest<T>(url, apiParams, opts).ready();
    }

    jsonRequest<T = any>(url: string, apiParams: APIParams, opts: RequestInit = {}): Promise<T> {
        return this._makeRequest<T>(url, apiParams, opts)
            .ready()
            .then((r: any) => {
                if (!!r && !!r.error) {
                    throw typeof r.error === 'string' ? r.error : r.error.id;
                }
                return r as T;
            });
    }

    _makeRequest<T = any>(url: string, apiParams: APIParams, opts: RequestInit = {}): AbortableRequest<T> {
        const qs = apiParams.getQueryString();

        return apiParams.isPOST() ?
            makeAbortablePOST(Config.softionAPI + url + (!!qs ? '?' + qs : ''), apiParams.getFormData() as any, opts)
            : makeAbortableGET(Config.softionAPI + url + (!!qs ? '?' + qs : ''), opts);
    }

    getUserTokenFromSession(): string | false {
        return PageDataFeed.user && (!!PageDataFeed.user.muToken ? PageDataFeed.user.muToken : false);
    }
}

export class BettingAPI extends APIPort {
    /** @type {AccountingAPI} */
    Accounting: AccountingAPI = new AccountingAPI(this);
    Admin: AdminAPI = new AdminAPI(this);
    Bonus: any = null;
    Services: ServicesAPI = new ServicesAPI(this);
    Sportsbook: any = null;
    /** @type {typeof WadminApi} */
    Wadmin: typeof WadminApi | null = null;

    constructor() {
        super();

        if (!!Config.publickey) {
            WadminApi.setPublic(Config.publickey);
        }

        WadminApi.setEndpoint(Config.softionAPI + '/wadmin/index.php');
        this.Wadmin = WadminApi;
    }

    /** @returns {typeof WadminApi} */
    WAdminAPI(): typeof WadminApi {
        return this.Wadmin!;
    }

    async apiPasswordRecoverySendSms(phone: string): Promise<any> {
        return await new Promise((resolve, reject) => {
            this.jsonRequest('/api_user/smsLostPassword', new APIRecoverySms(phone))
                .catch(reject)
                .then(body => !!body && !!body.error ? reject(body) : resolve(body));
        });
    }

    async apiPasswordRecoveryVerifyOTP(otpcode: string): Promise<any> {
        return await new Promise((resolve, reject) => {
            this.jsonRequest('/api_user/verifyOTP', new APIRecoveryOtp(otpcode))
                .catch(reject)
                .then(body => !!body && !!body.error ? reject(body) : resolve(body));
        });
    }

    async apiPasswordRecoveryAlterPassword(otpcode: string, newpassword: string, confirmpassword: string): Promise<any> {
        return await new Promise((resolve, reject) => {
            this.jsonRequest('/api_user/alterPassword', new APIRecoveryAlterPassword(otpcode, newpassword, confirmpassword))
                .catch(reject)
                .then(body => !!body && !!body.error ? reject(body) : resolve(body));
        });
    }

    async apiFirstLoginVerifyOTP(otpcode: string): Promise<any> {
        return await new Promise((resolve, reject) => {
            this.jsonRequest('/api_user/verifyLoginOTP', new APIRecoveryOtp(otpcode))
                .catch(reject)
                .then(body => !!body && !!body.error ? reject(body) : resolve(body));
        });
    }

    async apiLogin(username: string, password: string): Promise<UserInfoResponse> {
        let request: any = null;

        try {
            request = await (Promise as any).any([
                new Promise((resolve, reject) => {
                    this.jsonRequest('/api_user/login?dbg=yes', new APILoginParams(username, password))
                        .catch(reject)
                        .then(body => !!body && (body.login_status === 'logged' || body.login_status === 'link-sent') ? resolve(body) : reject(body));
                }),
                new Promise((resolve, reject) => {
                    this.jsonRequest('/wadmin/index.php?controller=adminuser&module=login&silent=y', new APIWadminLoginParams(username, password))
                        .catch(reject)
                        .then(body => {
                            if (!!body && body.login_status !== 'error') {
                                this.request(
                                    '/wadmin/index.php?controller=betting&module=betAsClub',
                                    new APIWadminAsClubParams(),
                                    { redirect: 'follow' }
                                )
                                    .catch(reject)
                                    .then(() => resolve(body))
                            } else {
                                reject(body);
                            }
                        });
                })
            ]);
        } catch (e) {
            request = null;
        }

        if (!request || !request.login_status) {
            throw 'error';
        }
        if (request.login_status !== 'logged') {
            throw request.login_status;
        }

        const accountid = Math.abs(request.userid);
        const accountaccess = !!request.manager ? (request.level === 5 ? 500 : 400) : 100;

        /** @type {import("../user.js").UserInfoResponse} */
        return {
            idstruttura: accountid,
            idcredenziale: accountid,
            company: request.user_nickname,
            username: request.user_nickname,
            type: !!request.manager ? 'affiliate' : 'player',
            idparent: null,
            path: '/' + accountid,
            path_len: 1,
            idaccess: accountaccess,
            tipo_accesso: accountaccess,
            preferred_currency: Config.defaultCurrency,
            created: null,
            token: null,
            notifications: !request.manager ? request.notifications : false,
            wallets: [{
                idwallet: accountid,
                idstruttura: accountid,
                idcredenziale: accountid,
                wallet_type: 'regular',
                wallet_subtype: 'regular',
                currency: Config.defaultCurrency,
                balance: request.user_balance,
                fido: !!request.user_fido ? request.user_fido : null,
                tot_in: null,
                tot_out: null
            }],
            bonus: request.wallets
        };
    }

    async tokenLogin(token: string): Promise<UserInfoResponse> {
        let request: any = null;

        try {
            request = await (Promise as any).any([
                new Promise((resolve, reject) => {
                    this.jsonRequest('/api_user/magicLinkLogin', new TokenLoginParams(token))
                        .catch(reject)
                        .then(body => !!body && body.login_status === 'logged' ? resolve(body) : reject(body));
                })
            ]);
        } catch (e) {
            request = null;
        }

        if (!request || !request.login_status || request.login_status !== 'logged') {
            throw 'error';
        }

        const accountid = Math.abs(request.userid);
        const accountaccess = !request ? (request.level === 5 ? 500 : 400) : 100;

        /** @type {import("../user.js").UserInfoResponse} */
        return {
            idstruttura: accountid,
            idcredenziale: accountid,
            company: request.user_nickname,
            username: request.user_nickname,
            type: !!request.manager ? 'affiliate' : 'player',
            idparent: null,
            path: '/' + accountid,
            path_len: 1,
            idaccess: accountaccess,
            tipo_accesso: accountaccess,
            preferred_currency: Config.defaultCurrency,
            created: null,
            token: null,
            notifications: !request.manager ? request.notifications : false,
            wallets: [{
                idwallet: accountid,
                idstruttura: accountid,
                idcredenziale: accountid,
                wallet_type: 'regular',
                wallet_subtype: 'regular',
                currency: Config.defaultCurrency,
                balance: request.user_balance,
                fido: !!request.user_fido ? request.user_fido : null,
                tot_in: null,
                tot_out: null
            }],
            bonus: request.wallets
        };
    }

    async apiLogout(): Promise<any> {
        await this.jsonRequest('/api_user/logout', new EmptyGETParams());
        return {};
    }

    async updateMyPassword(oldPassword: string, newPassword: string): Promise<UserInfoResponse> {
        const response: any = await this.jsonRequest('/user/password?json', new APIPasswordParams(oldPassword, newPassword));

        if (response.status !== 'success') {
            throw response.status;
        }

        return await this.getUserStatus();
    }

    async getUserStatus(withToken: boolean = false): Promise<UserInfoResponse> {
        const request: any = await this.jsonRequest('/api_user/info', new EmptyGETParams());
        if (!request) {
            throw JSON.stringify({ ex: "request is null", request })
        }
        const accountaccess = !!request.manager ? (request.level === 5 ? 500 : 400) : 100;
        const accountid = Math.abs(request.userid);
        /** @type {import("../user.js").UserInfoResponse} */
        return {
            idstruttura: accountid,
            idcredenziale: accountid,
            company: request.user_nickname,
            username: request.user_nickname,
            type: !!request.manager ? 'affiliate' : 'player',
            idparent: null,
            path: '/' + accountid,
            path_len: 1,
            ticketprint_url: request.ticketprint_url,
            idaccess: accountaccess,
            tipo_accesso: accountaccess,
            preferred_currency: Config.defaultCurrency,
            created: null,
            token: null,
            notifications: request.notifications,
            wallets: [{
                idwallet: accountid,
                idstruttura: accountid,
                idcredenziale: accountid,
                wallet_type: 'regular',
                wallet_subtype: 'regular',
                currency: Config.defaultCurrency,
                balance: request.user_balance,
                fido: !!request.user_fido ? request.user_fido : null,
                tot_in: null,
                tot_out: null
            }],
            bonus: request.wallets
        };
    }

    async registerStructUnder(idParent: number, role: string, username: string, password: string, confirmPassword: string, email: string | null = null, phone: string | null = null, other: any = {}): Promise<any> {
        const request: any = await this.jsonRequest('/admin/makenewuser?json', new APIRegistrationParams(username, password, confirmPassword, email, phone, other));
        return {
            username: request.user.username,
            ruolo: 'player',
            preferred_currency: request.user.currency,
            idaccess: 0,
            path_len: 1,
            idstruttura: request.user.id,
            path: "/" + request.user.id,
            idcredenziale: request.user.id,
            documents: []
        };
    }

    async registerUser(idParent: number, username: string, password: string, confirmPassword: string, email: string | null = null, phone: string | null = null, other: any = {}, otp: string | null = null): Promise<boolean> {
        const response: any = await this._makeRequest(
            '/api_user/completeRegistration',
            new WebsiteRegistrationParams(
                username, password, confirmPassword, email, phone,
                other.firstname || null, other.lastname || null, other.currency || null,
                other.country || null, other.gender || 'm', other.birthday || null,
                other.city || null, other.cap || null, other.address || null,
                other.agree_age || 'on', other.agree_privacy || 'on', other.agree_marketing || 'on',
                otp, idParent
            )
        ).ready();

        if (!!response.errors && !!Object.values(response.errors).length) {
            throw Object.keys(response.errors).join(', ');
        }

        return true;
    }

    /**
     * @throws {string} messggio d'errore 
     */
    async registerUserStart(idParent: number, username: string, password: string, confirmPassword: string, email: string | null = null, phone: string | null = null, other: any = {}): Promise<boolean> {
        const response: any = await this._makeRequest(
            '/otp/signupOTP',
            new WebsiteRegistrationParams(
                username, password, confirmPassword, email, phone,
                other.firstname || null, other.lastname || null, other.currency || null,
                other.country || null, other.gender || 'm', other.birthday || null,
                other.city || null, other.cap || null, other.address || null,
                other.agree_age || 'on', other.agree_privacy || 'on', other.agree_marketing || 'on',
                null, idParent
            )
        ).ready();

        if (response.status === 'error') {
            throw response.message;
        }

        return true;
    }
}

class AdminAPI extends APIPort {
    BettingApi: BettingAPI;

    constructor(BettingApi: BettingAPI) {
        super();
        this.BettingApi = BettingApi;
    }

    async getTransactions(fromDate: Date, toDate: Date, filter: any, page: number, perPage: number = 10, withInfo: boolean = false): Promise<any> {
        const lowlimit = (page * perPage) - perPage;
        // Metodo vuoto nel JS originale (commentato), implementazione parziale se necessaria.
        return await this.jsonRequest('/home/financial/' + lowlimit, new APIGetTransactionsParams(fromDate, toDate, filter, page, perPage, withInfo));
    }

    async getListPlayers(idstruttura: number = 0, username: string | null = null, page: number = 1, perPage: number = 10, withInfo: boolean = false): Promise<any> {
        const isUserSearchingById = username !== null && String(idstruttura) === String(username) && !isNaN(Number(username));
        const isUserSearchingByUsername = username !== null;
        const oldOperatorId = this.BettingApi.WAdminAPI().getOperatorId();

        if (idstruttura > 0 && !isUserSearchingById) {
            this.BettingApi.WAdminAPI().setOperatorId(idstruttura);
        }

        const response: any = await this.BettingApi.WAdminAPI().callCustomerMain(
            (page * perPage) - perPage,
            perPage,
            isUserSearchingById ? 2 : (isUserSearchingByUsername ? 1 : 0),
            isUserSearchingById ? 'on' : null,
            username
        ).ready();

        if (idstruttura > 0 && !isUserSearchingById) {
            this.BettingApi.WAdminAPI().setOperatorId(oldOperatorId);
        }

        const table = !!response.data && !!response.data.userSearchTable ? JSON.parse(response.data.userSearchTable) : null;

        return {
            status: 'success',
            code: '',
            data: !table || !table.data.length ? [] : table.data.map((item: any) => ({
                idcredenziale: item.id,
                idstruttura: item.id,
                idparent: item.parentid,
                path_len: 2,
                path: "/" + item.parentid + "/" + item.id,
                type: "player",
                shop: !!item.iscashier,
                idaccess: 100,
                company: item.username,
                preferred_currency: item.currency,
                created: !item.regdatets ? null : new Date(item.regdatets * 1000).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }),
                idskin: item.skinid,
                skin: item.skin,
                network: item.parent,
                email: item.email,
                account: [{
                    currency: item.currency,
                    balance: !!item.balance[item.currency] ? item.balance[item.currency] : 0.00,
                    fido: 0.00,
                    wallet_type: "regular",
                    tot_in: !item.allwinnings[item.currency] ? item.allwinnings[item.currency] : 0.00,
                    tot_out: !item.allwagers[item.currency] ? item.allwagers[item.currency] : 0.00
                }]
            })),
            ...(!withInfo || !table || !table.data.length ? {} : {
                _info: {
                    records: table.totalrecords
                }
            })
        };
    }

    async getListAffiliates(idParent: number|string): Promise<any[]> {
        const response: any = await this.BettingApi.WAdminAPI().callAdminuserChildren(idParent, 'on').ready();
        return response.data.output.map(
            (item: any) => ({
                idcredenziale: item.id,
                idstruttura: item.id,
                idparent: item.parentid,
                path_len: 2,
                path: "/" + item.parentid + "/" + item.id,
                type: item.type,
                idaccess: item.type === 'player' ? 100 : 500,
                shop: !!item.shop,
                company: item.company,
                preferred_currency: !!item.currency ? item.currency : Config.defaultCurrency,
                created: !item.regdatets ? null : new Date(item.regdatets * 1000).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }),
                idskin: !!item.skin ? item.skin.id : null,
                skin: !!item.skin ? item.skin.company : null,
                network: !!item.skin ? item.skin.company : null,
                account: Object.entries(item.balance).map(
                    ([currency, balance]) => ({
                        currency,
                        balance,
                        fido: !!item.fido && !!item.fido[currency] ? item.fido[currency] : 0.00,
                        wallet_type: "regular",
                        tot_in: 0,
                        tot_out: 0
                    })
                )
            })
        );
    }

    async getChildren(idParent: number|string): Promise<any[]> {
        const response: any = await this.BettingApi.WAdminAPI().callAdminuserChildren(idParent).ready();
        return response.data.output.map(
            (item: any) => ({
                id: item.id,
                name: item.company,
                type: item.type
            })
        );
    }

    async getDirectChildren(extended: number = 1): Promise<any[]> {
        const response: any[] = await this.jsonRequest('/scommesse/userList', new EmptyGETParams());
        return response.map(item => ({
            idstruttura: item.idaccounts,
            idparent: item.idaffiliates,
            path_len: 2,
            path: '/' + item.idaffiliates + '/' + item.idaccounts,
            type: 'player',
            idaccess: 100,
            company: item.username,
            preferred_currency: item.currency,
            created: item.iscrizione,
            username: item.username,
            tipo_accesso: 100,
            idcredenziale: item.idaccounts,
            account: [{
                currency: item.currency,
                balance: item.saldo,
                fido: 0.00,
                wallet_type: 'regular',
                tot_in: item.tot_in,
                tot_out: item.tot_out
            }],
            ...(extended != 1 ? {} : {
                documents: {
                    nome: null,
                    cognome: null,
                    email: item.email
                }
            })
        }));
    }
}

class AccountingAPI extends APIPort {
    BettingApi: BettingAPI;

    constructor(BettingApi: BettingAPI) {
        super();
        this.BettingApi = BettingApi;
    }

    async getWallets(idcredenziale: number, idstruttura: number | null = null): Promise<UserInfoResponse['wallets']> {
        const response: any = await this.BettingApi.WAdminAPI().callNewTransferMain(!!idcredenziale ? idcredenziale : idstruttura!).ready();
        const data = !!response.data.data ? response.data.data : {};
        return [
            {
                idwallet: data.id,
                idstruttura: data.id,
                idcredenziale: data.id,
                wallet_type: 'regular',
                wallet_subtype: 'regular',
                currency: data.currency,
                balance: data.disponibilita,
                fido: data.fido,
                tot_in: 0,
                tot_out: 0
            }
        ]
    }

    async getWalletsPlayer(idcredenziale: number, idstruttura: number | null = null): Promise<UserInfoResponse['wallets']> {
        const response: any = await this.BettingApi.WAdminAPI().callNewCustomerMain(!!idcredenziale ? idcredenziale : idstruttura!).ready();
        const data = !!response.data.user ? response.data.user : {};
        return [
            {
                idwallet: data.id,
                idstruttura: data.id,
                idcredenziale: data.id,
                wallet_type: 'regular',
                wallet_subtype: 'regular',
                currency: data.currency,
                balance: data.balance,
                fido: 0,
                tot_in: 0,
                tot_out: 0
            }
        ]
    }

    async getTransactions(fromDate: Date, toDate: Date, filter: any, page: number, perPage: number = 10, withInfo: boolean = false) {
        if (!!filter && !!filter.username) {
            const transactions: any = await this.BettingApi.WAdminAPI().callTransactionsViewAllTransactions(
                (page - 1) * perPage,
                perPage,
                0,
                fromDate.toLocaleDateString('en-CA'),
                toDate.toLocaleDateString('en-CA'),
                'on',
                filter.username,
                '0',
                '0',
                null
            ).ready();

            return TransactionsOutputFormat(
                !!transactions.data.searchTable ? transactions.data.searchTable : {},
                withInfo
            );
        }

        const lowlimit = (page * perPage) - perPage;
        const response: any = await this.jsonRequest('/home/financial/' + lowlimit, new APIGetTransactionsParams(fromDate, toDate, filter, page, perPage, withInfo));

        const convertCodeToString = (code: string, amount: number) => {
            switch (code) {
                case 'B': return 'ticket-placement';
                case 'O': return 'others';
                case 'C':
                case 'W': return 'ticket-winner';
                case 'S': return amount < 0 ? 'slot-spin-bet' : 'slot-spin-win';
                case 'Z': return 'manual-movement';
                default: return code;
            }
        };

        const convertDettaglioToDescriptionObj = (dettaglio: string, code: string, amount: number) => {
            const extractedDettaglio = MovementDetailsTranslator.extractInfoFromDetailBody(dettaglio)
            if (extractedDettaglio == null) {
                return {
                    message: convertCodeToString(code, amount),
                    rawmessage: dettaglio,
                    data: dettaglio,
                };
            }
            return {
                message: extractedDettaglio.type,
                rawmessage: dettaglio,
                data: extractedDettaglio.data
            }
        }

        return {
            status: 'success',
            code: '',
            data: !response.records.length ? [] : response.records.map((item: any) => {
                const isSender = parseFloat(item.amount) < 0;
                return {
                    ts: item.datets,
                    description: convertDettaglioToDescriptionObj(item.description, item.reason, parseFloat(item.amount)),
                    idtransazione: item.id,
                    idsender: isSender ? item.idaccounts : 0,
                    idwallet_sender: isSender ? item.idaccounts : 0,
                    idreceiver: !isSender ? item.idaccounts : 0,
                    idwallet_receiver: !isSender ? item.idaccounts : 0,
                    currency: item.display_currency,
                    amount: item.amount,
                    fido: "0.00",
                    new_sender_balance: isSender ? item.balance : 0,
                    new_sender_fido: "0.00",
                    new_receiver_balance: !isSender ? item.balance : 0,
                    new_receiver_fido: "0.00",
                    search_code: item.reason + ":" + item.id,
                    sender: {
                        idstruttura: isSender ? item.idaccounts : 0,
                        idparent: null,
                        path_len: isSender ? 1 : 0,
                        path: isSender ? "/" + item.idaccounts : 0,
                        type: isSender ? "player" : null,
                        idaccess: isSender ? 100 : null,
                        company: isSender ? item.username : null,
                        preferred_currency: isSender ? item.display_currency : null,
                        created: null
                    },
                    receiver: {
                        idstruttura: !isSender ? item.idaccounts : 0,
                        idparent: null,
                        path_len: !isSender ? 1 : 0,
                        path: !isSender ? "/" + item.idaccounts : 0,
                        type: !isSender ? "player" : null,
                        idaccess: !isSender ? 100 : null,
                        company: !isSender ? item.username : null,
                        preferred_currency: !isSender ? item.display_currency : null,
                        created: null
                    },
                    "is-sender": isSender,
                    wallet_type: Math.abs(parseFloat(item.bonus)) > 0 ? "bonus" : "regular",
                    wallet_subtype: Math.abs(parseFloat(item.bonus)) > 0 ? "bonus" : "regular"
                };
            }),
            totals: {
                out: response.total_negative_amount,
                in: response.total_positive_amount,
                tot: response.trans_total_amount,
            },
            page_totals: {
                out: response.trans_page_negative_amount,
                in: response.trans_page_positive_amount,
                tot: response.trans_page_amount,
            },
            ...(!withInfo ? {} : {
                _info: {
                    records: response.betcount
                }
            })
        };
    }

    async getSubAccountTransactions(idPlayer: number, filter: any, fromDate: Date, toDate: Date, page: number = 1, perPage: number = 30, withInfo: boolean = false): Promise<any> {
        const transactions: any = await this.BettingApi.WAdminAPI().callTransactionsViewAllTransactions(
            (page - 1) * perPage,
            perPage,
            0,
            fromDate.toLocaleDateString('en-CA'),
            toDate.toLocaleDateString('en-CA'),
            !!filter && !!filter.username ? 'on' : null,
            !!filter && !!filter.username ? filter.username : '_',
            '0',
            '0',
            idPlayer
        ).ready();

        return TransactionsOutputFormat(
            !!transactions.data.searchTable ? transactions.data.searchTable : {},
            withInfo
        );
    }

    async getMovimentiRete(idPlayer: number, idCredenziale: number, fromDate: string, toDate: string, page: number = 1, perPage: number = 10, filter: any, withInfo: boolean = false): Promise<any> {
        const transactions: any = await this.BettingApi.WAdminAPI().callTransactionsAffiliateTransactionSearch(
            (page - 1) * perPage,
            perPage,
            0,
            fromDate,
            toDate,
            !!filter && !!filter.username ? 'on' : null,
            !!filter && !!filter.username ? filter.username : '_'
        ).ready();

        const convertDettaglioToDescriptionObj = (dettaglio: string, code: string, amount: number) => {
            const extractedDettaglio = MovementDetailsTranslator.extractInfoFromDetailBody(dettaglio)
            if (extractedDettaglio == null) {
                return {
                    message: code,
                    rawmessage: dettaglio,
                    data: dettaglio
                };
            }
            return {
                message: extractedDettaglio.type,
                rawmessage: dettaglio,
                data: extractedDettaglio.data
            }
        }

        const response = !!transactions.data.searchTable ? transactions.data.searchTable : {};

        return {
            status: 'success',
            code: '',
            data: !response.data.length ? [] : response.data.map((item: any) => {
                const currency = !!item.currency ? item.currency : Config.defaultCurrency;
                const sender = item.source;
                const receiver = item.target;

                return {
                    ts: item.createdts,
                    description: convertDettaglioToDescriptionObj(item.description || item.transtypedesc, item.transtype, parseFloat(item.amount)),
                    idtransazione: item.id,
                    idsender: sender.affiliateid,
                    idwallet_sender: sender.affiliateid,
                    idreceiver: receiver.affiliateid,
                    idwallet_receiver: receiver.affiliateid,
                    currency: currency,
                    amount: item.amount,
                    fido: "0.00",
                    new_sender_balance: sender.balance,
                    new_sender_fido: sender.fido,
                    new_receiver_balance: receiver.balance,
                    new_receiver_fido: receiver.fido,
                    search_code: item.transtypeid + ":" + item.id,
                    sender: {
                        idstruttura: sender.affiliateid,
                        idparent: null,
                        path_len: 1,
                        path: "/" + sender.affiliateid,
                        type: sender.idlevel === null ? "player" : "affiliate",
                        idaccess: sender.idlevel === null ? 100 : 500,
                        company: sender.company,
                        preferred_currency: !!sender.currency ? sender.currency : Config.defaultCurrency,
                        created: null
                    },
                    receiver: {
                        idstruttura: receiver.affiliateid,
                        idparent: null,
                        path_len: 1,
                        path: "/" + receiver.affiliateid,
                        type: receiver.idlevel === null ? "player" : "affiliate",
                        idaccess: receiver.idlevel === null ? 100 : 500,
                        company: receiver.company,
                        preferred_currency: !!receiver.currency ? receiver.currency : Config.defaultCurrency,
                        created: null
                    },
                    "is-sender": item.amount >= 0,
                    wallet_type: "regular",
                    wallet_subtype: "regular"
                };
            }),
            ...(!withInfo ? {} : {
                _info: {
                    records: response.totalrecords
                }
            })
        };
    }

    async performTransactionsNet(idStrutturaOrigin: number, idCredenzialeTarget: number, idStrutturaTarget: number, amount: number, currency: string, notes: string = '', fido: boolean = false): Promise<any> {
        const response: any = await this.BettingApi.WAdminAPI().callNewTransferMakeTransfer(
            idStrutturaTarget,
            currency,
            Math.abs(amount),
            amount > 0 ? 'n' : 'y',
            !!fido ? 'fido' : 'saldo',
            notes
        ).ready();

        if (!!response.error) {
            throw response.error;
        }

        const transactionId = response.idtrans;
        const transactionDate = response.ts;
        const senderId = amount > 0 ? idStrutturaOrigin : idStrutturaTarget;
        const receiverId = amount > 0 ? idStrutturaTarget : idStrutturaOrigin;

        const WalletResponse = await Promise.all([
            this.getWallets(senderId),
            this.getWallets(receiverId)
        ]);

        let WalletSender = WalletResponse[0].find(w => w.currency === currency) || WalletResponse[0][0];
        let WalletReceiver = WalletResponse[1].find(w => w.currency === currency) || WalletResponse[1][0];

        return {
            idtransazione: transactionId,
            idsender: senderId,
            idwallet_sender: senderId,
            idreceiver: receiverId,
            idwallet_receiver: receiverId,
            ts: transactionDate,
            currency,
            amount: Math.abs(amount),
            fido: "0.0000",
            new_sender_balance: WalletSender.balance,
            new_sender_fido: "0.0000",
            new_receiver_balance: WalletReceiver.balance,
            new_receiver_fido: "0.0000",
            search_code: "0:" + (amount > 0 ? 'D' : 'W') + ":-",
            description: JSON.stringify({
                message: 'manual-movement',
                data: {
                    description: notes,
                    desposit: amount > 0
                },
            })
        };
    }

    async performTransactions(idStrutturaOrigin: number|string, idCredenzialeTarget: number|string, idStrutturaTarget: number|string, amount: number, currency: string, notes: string = '', fido: boolean = false): Promise<any> {
        const response: any = await this.BettingApi.WAdminAPI().callNewCustomerGiveBonus(
            idStrutturaTarget,
            currency,
            Math.abs(amount),
            amount > 0 ? 'n' : 'y',
            notes
        ).ready();

        if (!!response.error) {
            throw response.error;
        }

        const transactionId = response.idtrans;
        const transactionDate = response.ts;
        const senderId = idStrutturaOrigin;
        const receiverId = idStrutturaTarget;

        const WalletResponse = await Promise.all([
            this.getWallets(parseInt(String(senderId))),
            this.getWalletsPlayer(parseInt(String(receiverId))),
        ]);

        let WalletSender = WalletResponse[0].find(w => w.currency === currency) || WalletResponse[0][0];
        let WalletReceiver = WalletResponse[1].find(w => w.currency === currency) || WalletResponse[1][0];

        return {
            idtransazione: transactionId,
            idsender: senderId,
            idwallet_sender: senderId,
            idreceiver: receiverId,
            idwallet_receiver: receiverId,
            ts: transactionDate,
            currency,
            amount: Math.abs(amount),
            fido: "0.0000",
            new_sender_balance: WalletSender.balance,
            new_sender_fido: "0.0000",
            new_receiver_balance: WalletReceiver.balance,
            new_receiver_fido: "0.0000",
            search_code: "0:" + (amount > 0 ? 'D' : 'W') + ":-",
            description: JSON.stringify({
                message: 'manual-movement',
                data: {
                    description: notes,
                    desposit: amount > 0
                },
            })
        };
    }

    async performTransactionToUsername(idStrutturaOrigin: number|string, usernameTarget: string, amount: number, currency: string, notes: string = '', fido: boolean = false): Promise<any> {
        const request: any = await this.jsonRequest('/admin/userstransaction?json', new APIUsernameTransactionParams(parseInt(String(idStrutturaOrigin)), usernameTarget, amount, currency, notes, fido));
        return {
            idtransazione: request['user-transaction-id'],
            idsender: 0,
            idwallet_sender: 0,
            idreceiver: 0,
            idwallet_receiver: 0,
            ts: request["ts"],
            currency,
            amount: Math.abs(amount),
            fido: "0.0000",
            new_sender_balance: 0,
            new_sender_fido: "0.0000",
            new_receiver_balance: 0,
            new_receiver_fido: "0.0000",
            search_code: "0:" + (amount > 0 ? 'D' : 'W') + ":-",
            description: JSON.stringify({
                message: 'manual-movement',
                data: {
                    description: notes,
                    desposit: amount > 0
                },
            })
        };
    }
}

class ServicesAPI extends APIPort {
    BettingApi: BettingAPI;
    constructor(BettingApi: BettingAPI) {
        super();
        this.BettingApi = BettingApi;
    }
    async buyCurrency(amount: number, currency: string, payWith: string): Promise<void> {
        // Implementazione assente nel JS
    }
    async sellCurrency(amount: number, currency: string, returnCurrency: string): Promise<void> {
        // Implementazione assente nel JS
    }
}

const TransactionsOutputFormat = (response: any, withInfo: boolean) => {
    const convertCodeToString = (code: string, amount: number) => {
        switch (code) {
            case 'B': return 'ticket-placement';
            case 'O': return 'others';
            case 'C':
            case 'W': return 'ticket-winner';
            case 'S': return amount < 0 ? 'slot-spin-bet' : 'slot-spin-win';
            case 'Z': return 'manual-movement';
            case 'V': return 'deposit';
            default: return code;
        }
    };
    const convertDettaglioToDescriptionObj = (dettaglio: string, code: string, amount: number) => {
        const extractedDettaglio = MovementDetailsTranslator.extractInfoFromDetailBody(dettaglio)
        if (extractedDettaglio == null) {
            return {
                message: convertCodeToString(code, amount),
                rawmessage: dettaglio,
                data: dettaglio
            };
        }
        return {
            message: extractedDettaglio.type,
            rawmessage: dettaglio,
            data: extractedDettaglio.data
        }
    }

    return {
        status: 'success',
        code: '',
        data: !response.data.length ? [] : response.data.map((item: any) => {
            const currency = !!item.display_currency ? item.display_currency : Config.defaultCurrency;
            const amount = !!item.display_amount ? item.display_amount : item.amount;
            const balance = !!item.display_balance ? item.display_balance : item.balance;
            const isSender = parseFloat(amount) < 0;
            return {
                ts: Math.round(new Date(item.created).getTime() / 1000),
                description: convertDettaglioToDescriptionObj(item.description || item.dettaglio, item.transtypeid, parseFloat(amount)),
                idtransazione: item.transactionid,
                idsender: isSender ? item.destuserid : 0,
                idwallet_sender: isSender ? item.destuserid : 0,
                idreceiver: !isSender ? item.destuserid : 0,
                idwallet_receiver: !isSender ? item.destuserid : 0,
                currency: currency,
                amount: amount,
                fido: "0.00",
                new_sender_balance: isSender ? balance : 0,
                new_sender_fido: "0.00",
                new_receiver_balance: !isSender ? balance : 0,
                new_receiver_fido: "0.00",
                search_code: item.transtypeid + ":" + item.transactionid,
                sender: {
                    idstruttura: isSender ? item.destuserid : 0,
                    idparent: null,
                    path_len: isSender ? 1 : 0,
                    path: isSender ? "/" + item.destuserid : 0,
                    type: isSender ? "player" : null,
                    idaccess: isSender ? 100 : null,
                    company: isSender ? item.username : null,
                    preferred_currency: isSender ? currency : null,
                    created: null
                },
                receiver: {
                    idstruttura: !isSender ? item.destuserid : 0,
                    idparent: null,
                    path_len: !isSender ? 1 : 0,
                    path: !isSender ? "/" + item.destuserid : 0,
                    type: !isSender ? "player" : null,
                    idaccess: !isSender ? 100 : null,
                    company: !isSender ? item.username : null,
                    preferred_currency: !isSender ? currency : null,
                    created: null
                },
                "is-sender": isSender,
                wallet_type: "regular",
                wallet_subtype: "regular"
            };
        }),
        ...(!withInfo ? {} : {
            _info: {
                records: response.totalrecords
            }
        })
    };
};


class EmptyGETParams implements APIParams {
    isPOST() { return false; }
    constructor() { }
    getQueryString() { return ''; }
    getFormData(): FormData | null { return null; }
}

class EmptyPOSTParams extends EmptyGETParams {
    isPOST() { return true; }
}

class APILoginParams extends EmptyPOSTParams {
    username = '';
    password = '';

    constructor(username: string, password: string) {
        super();
        this.username = username;
        this.password = password;
    }

    getFormData() {
        const form = new FormData();
        form.append('login_username', this.username);
        form.append('login_password', this.password);
        return form;
    }
}

class TokenLoginParams extends EmptyPOSTParams {
    token = '';

    constructor(token: string) {
        super();
        this.token = token;
    }

    getFormData() {
        const form = new FormData();
        form.append('token', this.token);
        return form;
    }
}

class APIPasswordParams extends EmptyPOSTParams {
    oldpassword = '';
    newpassword = '';

    constructor(oldpassword: string, newpassword: string) {
        super();
        this.oldpassword = oldpassword;
        this.newpassword = newpassword;
    }

    getFormData() {
        const form = new FormData();
        form.append('oldpassword', this.oldpassword);
        form.append('newpassword', this.newpassword);
        form.append('confirmpassword', this.newpassword);
        return form;
    }
}

class APIWadminLoginParams extends APILoginParams {
    getFormData() {
        const form = new FormData();
        form.append('username', this.username);
        form.append('password', this.password);
        return form;
    }
}

class APIWadminAsClubParams extends APILoginParams {
    constructor() { super('', ''); }
    getFormData() { return new FormData(); }
}

class APIGetTransactionsParams extends EmptyGETParams {
    fromDate: Date;
    toDate: Date;
    filter: any;
    page: number;
    perPage: number;
    withInfo: boolean;

    constructor(fromDate: Date, toDate: Date, filter: any, page: number, perPage: number, withInfo: boolean = false) {
        super();
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.filter = filter;
        this.page = page;
        this.perPage = perPage;
        this.withInfo = withInfo;
    }

    getQueryString() {
        const fromDateStr = !!this.fromDate ? this.fromDate.toLocaleDateString('en-CA') : '';
        const toDateStr = !!this.toDate ? this.toDate.toLocaleDateString('en-CA') : '';

        const form = new URLSearchParams();
        form.append('searchStartDate', fromDateStr);
        form.append('searchEndDate', toDateStr);
        form.append('searchHelper', '5');
        form.append('perPage', String(this.perPage));
        if (!!this.filter && !!this.filter.reason) {
            form.append("searchReason", this.filter.reason);
        }
        return 'json&' + form.toString();
    }
}

class APIRecoverySms extends EmptyGETParams {
    phone = '';
    constructor(phone: string) {
        super();
        this.phone = phone;
    }

    getQueryString() {
        const form = new URLSearchParams();
        form.append('phone', this.phone);
        return 'json&' + form.toString();
    }
}

class APIRecoveryOtp extends EmptyGETParams {
    otp = '';
    constructor(otp: string) {
        super();
        this.otp = otp;
    }

    getQueryString() {
        const form = new URLSearchParams();
        form.append('code', this.otp);
        return 'json&' + form.toString();
    }
}

class APIRecoveryAlterPassword extends EmptyPOSTParams {
    otpcode = '';
    password = '';
    confirmPassword = '';

    constructor(otpcode: string, password: string, confirmpassword: string) {
        super();
        this.otpcode = otpcode;
        this.password = password;
        this.confirmPassword = confirmpassword;
    }

    getFormData() {
        const form = new FormData();
        form.append('code', this.otpcode);
        form.append('new-password', this.password);
        form.append('confirm-pass', this.confirmPassword);
        return form;
    }
}

class WebsiteRegistrationParams extends EmptyPOSTParams {
    constructor(
        public username: string,
        public password: string,
        public confirmPassword: string,
        public email: string | null,
        public phone: string | null,
        public firstname: string | null,
        public lastname: string | null,
        public currency: string | null,
        public country: string | null,
        public gender: string | null,
        public birthday: string | null,
        public city: string | null,
        public cap: string | null,
        public address: string | null,
        public agree_age: string | null,
        public agree_privacy: string | null,
        public agree_marketing: string | null,
        public otp: string | null,
        public codaff: number | string | null,
    ) {
        super();
    }

    getFormData() {
        const form = new FormData();

        const data: Record<string, any> = {
            "username": this.username,
            "password": this.password,
            "confpassword": this.password,
            "email": this.email,
            "firstname": this.firstname,
            "lastname": this.lastname,
            "currency": this.currency,
            "country": this.country,
            "gender": this.gender,
            "birthday": this.birthday,
            "city": this.city,
            "cap": this.cap,
            "address": this.address,
            "phone": this.phone,
            "agree_age": this.agree_age,
            "agree_privacy": this.agree_privacy,
            "agree_marketing": this.agree_marketing,
            "otp": this.otp
        };

        Object.entries(data).forEach(([key, value]) => {
            if (value !== null) form.append(key, value);
        });
        
        if (this.codaff != 0 && this.codaff !== null)
            form.append('codaff', String(this.codaff));

        return form;
    }
}

class APIRegistrationParams extends EmptyPOSTParams {
    constructor(
        public username: string,
        public password: string,
        public confirmPassword: string,
        public email: string | null,
        public phone: string | null,
        public other: any
    ) {
        super();
    }

    getFormData() {
        const form = new FormData();

        const data: Record<string, any> = {
            "new-username": this.username,
            "new-password": this.password,
            "new-amount": "0",
            "new-firstname": this.other.nome,
            "new-lastname": this.other.cognome,
            "new-birthcountry": this.other.nascita_nazione,
            "new-birthstate": this.other.nascita_provincia,
            "new-birthcity": this.other.nascita_luogo,
            "new-birthdate": this.other.nascita_data,
            "new-fiscalcode": '',
            "new-email": this.email,
            "new-tel": this.phone,
            "new-country": this.other.residenza_nazione,
            "new-state": this.other.residenza_provincia,
            "new-address": this.other.residenza_indirizzo + (!!this.other.residenza_numcivico ? ' N.' + this.other.residenza_numcivico : ''),
            "new-zipcode": this.other.residenza_cap,
            "new-city": this.other.residenza_citta
        };

        Object.entries(data).forEach(([key, value]) => form.append(key, value));

        return form;
    }
}

class APIUsernameTransactionParams extends EmptyPOSTParams {
    constructor(
        public idCredenzialeTarget: number,
        public usernameTarget: string,
        public amount: number,
        public currency: string,
        public notes: string,
        public fido: boolean | string
    ) {
        super();
    }

    getFormData() {
        const form = new FormData();
        form.append('username', this.usernameTarget);
        form.append('trans-amount', String(Math.abs(this.amount)));
        form.append('trans-type', this.amount < 0 ? 'P' : 'V');
        form.append('trans-details', this.notes);
        return form;
    }
}

class MovementDetailsTranslator {
    static traduzioni_dettagli: [string, RegExp][] = [
        ["ticket-win-rollback", /^Aggiustamento Importo Scommessa\nRestituiti per TicketID: (.*?)$/],
        ["ticket-bet-edit-refused-timeout", /^Scommessa rifiutata dall'utente per timeout\nTicketID: (.*?)$/],
        ["affiliate-withdraw", /^Scarico Cash: Azzeramento saldo per inizio attività\. M\.$/],
        ["ticket-bet-edit-refused", /^Scommessa Rifiutata Dall'Utente\. \nTicketID: (.*?)$/],
        ["affiliate-deposit", /^Carico Fido (.*?): Fido per inizio attività$/],
        ["ticket-cancel", /^Scommessa Annullata ?\nTicketID: (.*?)$/],
        ["ticket-refused", /^Scommessa Rifiutata\nTicketID: (.*?)$/],
        ["affiliate-transaction", /^Movimentazione conto cliente: (.*?);?$/],
        ["monthly-reward", /^Provvigione: (.*?) - bonus mensile$/],
        ["weekly-reward", /^Provvigione: (.*?) - bonus settimanale$/],
        ["ticket-win", /^Scommessa (?:[^\n]+?)\nTicketID: (.*?)$/],
        ["service-virtual-deposit", /^Trasferimento a Virtual$/],
        ["service-poker-deposit", /^Trasferimento a Poker$/],
        ["service-virtual-withdraw", /^Prelievo da Virtual$/],
        ["service-poker-withdraw", /^Prelievo da Poker$/],
        ["service-casino-withdraw", /^Storno su Slot (.*?)$/],
        ["deposit", /^Ricarica Utente$/],
        ["service-casino-spin", /^Tiro su Slot (.*?)$/],
        ["affiliate-deposit", /^Carico Fido (.*?)$/],
        ["ticket-bet-placed", /^Ticket ID: (.*?)$/],
        ["affiliate-deposit", /^Deposito a (.*?)$/],
        ["withdraw", /^Scarico Cash: (.*?)$/],
        ["service-casino-spin", /^Tiro Slot (.*?)$/],
        ["deposit", /^Carico Cash: (.*?)$/],
        ["ticket-bet-placed", /TicketID: (.*?)$/],
        ["voucher-created", /^Creazione Voucher$/],
    ];

    static extractInfoFromDetailBody(dettaglio: string): { type: string, data: string } | null {
        const firstMatchingRow = this._findFirstMatch(dettaglio);
        if (!firstMatchingRow) {
            return null;
        }
        const match = dettaglio.match(firstMatchingRow[1]);
        return {
            type: firstMatchingRow[0],
            data: match ? match[1] : ''
        };
    }

    static _findFirstMatch(dettaglio: string) {
        return this.traduzioni_dettagli.find(row => row[1].test(dettaglio))
    }
}

const DESCS = {
    'ticket-win-rollback': (data) => <Trans>Ticket Rollback {data}</Trans>,
    'ticket-bet-edit-refused-timeout': (data) => <Trans>Refused Ticket Edit {data}</Trans>,
    'affiliate-withdraw': () => <Trans>Affiliate Withdraw</Trans>,
    'ticket-bet-edit-refused': (data) => <Trans>Refused Ticket Edit {data}</Trans>,
    'affiliate-deposit': (data) => <Trans>Affiliate Deposit: {data}</Trans>,
    'ticket-cancel': (data) => <Trans>Ticket Cancelled: {data}</Trans>,
    'ticket-refused': (data) => <Trans>Ticket Refused: {data}</Trans>,
    'affiliate-transaction': (data) => <Trans>Client Account Transaction: {data}</Trans>,
    'monthly-reward': (data) => <Trans>Monthly Reward: {data}</Trans>,
    'weekly-reward': (data) => <Trans>Weekly Reward: {data}</Trans>,
    'ticket-win': (data) => <Trans>Ticket Win: {data}</Trans>,
    'service-virtual-deposit': () => <Trans>Transfer to Virtual</Trans>,
    'service-poker-deposit': () => <Trans>Transfer to Poker</Trans>,
    'service-virtual-withdraw': () => <Trans>Withdraw from Virtual</Trans>,
    'service-poker-withdraw': () => <Trans>Withdraw from Poker</Trans>,
    'service-casino-withdraw': (data) => <Trans>Casino Withdraw: {data}</Trans>,
    'deposit': (data = null) => <Trans>User Deposit {data ? `(${data})` : ''}</Trans>,
    'service-casino-spin': (data) => <Trans>Slot Spin: {data}</Trans>,
    'ticket-bet-placed': (data) => <Trans>Ticket Bet Placed: {data}</Trans>,
    'withdraw': (data) => <Trans>Cash Withdraw: {data}</Trans>,
    'ticket-placement': (data) => <Trans>Ticket: {data}</Trans>,
    'others': (data) => <Trans>{data}</Trans>,
    'ticket-winner': (data) => <Trans>Ticket win: {data}</Trans>,
    'slot-spin-bet': (data) => <Trans>Casino Bet {data}</Trans>,
    'slot-spin-win': (data) => <Trans>Casino Win {data}</Trans>,
    'voucher-creted': (data) => <Trans>Voucher Withdraw</Trans>,
}


export function Description({ type, data, raw }: { type: string, data?: any, raw?: any }) {
    return DESCS[type] ? h(Fragment, null, DESCS[type](data || '')) : raw;
}